<script setup>
import ActionButton from "@js/components/ActionButton.vue";
import AlertBox from "@js/components/AlertBox.vue";
import Drawer from "@js/components/Drawer.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { useRoute } from "@js/utils/route";
import moment from "moment";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { date, object, string } from "yup";
import EditorField from "@js/components/fields/EditorField.vue";

const { t } = useI18n();

const { route } = useRoute();

const contentTypes = [
  {
    value: "standard",
    label: t("label.standard"),
  },
  {
    value: "premium",
    label: t("label.premium"),
  },
];

const { tenant, eCommerceCategory, seoApikomContent, callback } = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
  eCommerceCategory: {
    type: Object,
    required: true,
  },
  seoApikomContent: {
    type: Object,
    required: false,
  },
  callback: {
    type: Function,
    required: false,
  },
});

const isSubmitting = ref(false);

const isOpen = ref(false);

const selectedTab = ref(0);

const form = reactive({
  contentType: eCommerceCategory.seoContentCategory
    ? contentTypes.find(
        (contentType) =>
          contentType.value ===
          eCommerceCategory.seoContentCategory.contentType.value,
      )
    : seoApikomContent
      ? contentTypes.find(
          (contentType) => contentType.value === seoApikomContent.type.value,
        )
      : contentTypes[0],
  name: eCommerceCategory.languageName,
  description: eCommerceCategory.languageDescription,
  descriptionUpdatedAt: eCommerceCategory.seoContentCategory
    ? eCommerceCategory.seoContentCategory.descriptionUpdatedAt
    : moment().format("YYYY-MM-DD"),
  metaTitle: eCommerceCategory.languageMetaTitle,
  metaDescription: eCommerceCategory.languageMetaDescription,
});

const validationSchema = object({
  contentType: object()
    .shape({
      value: string().required(t("error.required")),
      label: string().required(t("error.required")),
    })
    .required(t("error.required")),
  name: string().required(t("error.required")),
  description: string().nullable(),
  descriptionUpdatedAt: date().nullable(),
  metaTitle: string().nullable(),
  metaDescription: string().nullable(),
});

function changeTab(tab) {
  selectedTab.value = tab;
}

function setIsOpen(value) {
  isOpen.value = value;
}

function updateAction() {
  isSubmitting.value = true;

  fetch(
    route("front.projectManagement.seo.postUpdateECommerceCategoryContents", {
      tenant: tenant.id,
    }),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        seoContentCategoryContentType: form.contentType.value,
        cmsCategoryId: eCommerceCategory.cmsCategoryId,
        name: form.name,
        description: form.description,
        seoContentCategoryDescriptionUpdatedAt: form.descriptionUpdatedAt,
        metaTitle: form.metaTitle,
        metaDescription: form.metaDescription,
      }),
    },
  )
    .then((response) => response.json())
    .then(() => {
      setIsOpen(false);

      if (callback) {
        callback();
      }
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      isSubmitting.value = false;
    });
}
</script>

<template>
  <slot name="button" :set-is-open="setIsOpen" />

  <Drawer :is-open="isOpen" :set-is-open="setIsOpen" size="almost-full">
    <template #title>
      <div class="flex items-center">
        {{ t("title.update") }}
      </div>
    </template>

    <template #full-content>
      <Form
        :validation-schema="validationSchema"
        @submit="updateAction"
        v-slot="{ errors }"
        class="flex h-full flex-col"
      >
        <div class="h-full max-h-[calc(100vh-8rem)] overflow-y-auto px-6 py-4">
          <div class="flex gap-4 flex-col">
            <AlertBox
              v-if="
              (seoApikomContent &&
                seoApikomContent.type.value !== form.contentType.value) ||
              (eCommerceCategory.seoApikomContent &&
                eCommerceCategory.seoApikomContent.type.value !==
                  form.contentType.value)
            "
              type="danger"
            >
              {{ t("text.contentTypeAreNotTheSame") }}
            </AlertBox>

            <AlertBox
              type="warning"
            >
              {{ t("text.thinkToUpdateDescriptionUpdatedAt") }}
            </AlertBox>
          </div>

          <TabGroup
            :selected-tab="selectedTab"
            :change-tab="changeTab"
            :tabs="{
              information: t('title.information'),
            }"
            disable-default-tab-change
            disable-url-change
            disable-open-new-tab
          >
            <template #panels>
              <TabPanel without-background>
                <InputField
                  v-model="form.metaTitle"
                  :label="t('label.metaTitle')"
                  field-name="metaTitle"
                  field-id="metaTitle"
                  required
                />

                <InputField
                  v-model="form.metaDescription"
                  :label="t('label.metaDescription')"
                  field-name="metaDescription"
                  field-id="metaDescription"
                />

                <InputField
                  v-model="form.name"
                  :label="t('label.name')"
                  field-name="name"
                  field-id="name"
                  required
                />

                <ListboxField
                  v-model="form.contentType"
                  field-id="contentType"
                  field-name="contentType"
                  :label="t('label.contentType')"
                  :options="contentTypes"
                  by="value"
                  required
                >
                  <template #buttonContent>
                    {{ form.contentType.label }}
                  </template>

                  <template #optionContent="{ option }">
                    {{ option.label }}
                  </template>
                </ListboxField>

                <InputField
                  v-model="form.descriptionUpdatedAt"
                  :label="t('label.descriptionUpdatedAt')"
                  field-name="descriptionUpdatedAt"
                  field-id="descriptionUpdatedAt"
                  type="date"
                />

                <EditorField
                  v-model="form.description"
                  :label="t('label.description')"
                  field-name="description"
                  field-id="description"
                />
              </TabPanel>
            </template>
          </TabGroup>
        </div>

        <div
          class="mt-auto flex flex-wrap items-center gap-2 border-t border-t-gray-200 p-6"
        >
          <ActionButton
            submit
            :disabled="Object.keys(errors).length > 0 || isSubmitting"
            :loading="isSubmitting"
          >
            <template v-if="!isSubmitting">
              {{ t("button.save") }}
            </template>

            <template v-else>
              {{ t("button.saveInProgress") }}
            </template>
          </ActionButton>
        </div>
      </Form>
    </template>
  </Drawer>
</template>
